import { useState, useCallback } from 'react';

/**
 * Hook for managing mandatory closure comments on subtasks.
 * Handles the modal state and async flow: comment first, then status change.
 *
 * @param {function} addCommentFn - Function to add a comment: (taskId, comment) => Promise
 * @returns {object} - closureModal state and control functions
 */
const useClosureComment = (addCommentFn) => {
    const [closureModal, setClosureModal] = useState({
        isOpen: false,
        taskId: null,
        taskTitle: '',
        isMeeting: false,
        statusId: null,
        callback: null
    });
    const [isSubmittingClosure, setIsSubmittingClosure] = useState(false);

    /**
     * Opens the closure comment modal and stores the completion callback.
     */
    const requestClosure = useCallback((taskId, taskTitle, isMeeting, statusId, onComplete) => {
        setClosureModal({
            isOpen: true,
            taskId,
            taskTitle,
            isMeeting: isMeeting || false,
            statusId,
            callback: onComplete
        });
    }, []);

    /**
     * Submits the closure comment, then calls the stored callback to complete status change.
     */
    const confirmClosure = useCallback(async (comment) => {
        if (!closureModal.taskId || !addCommentFn) return;

        setIsSubmittingClosure(true);
        try {
            // Add the closure comment first
            await addCommentFn(closureModal.taskId, comment, true);

            // Execute the status change callback
            if (closureModal.callback) {
                await closureModal.callback();
            }

            // Close modal on success
            setClosureModal({
                isOpen: false,
                taskId: null,
                taskTitle: '',
                isMeeting: false,
                statusId: null,
                callback: null
            });
        } catch (err) {
            console.error('Closure comment failed:', err);
            alert('Failed to add closure comment. Please try again.');
        } finally {
            setIsSubmittingClosure(false);
        }
    }, [closureModal, addCommentFn]);

    /**
     * Cancels the closure action and closes the modal.
     */
    const cancelClosure = useCallback(() => {
        setClosureModal({
            isOpen: false,
            taskId: null,
            taskTitle: '',
            isMeeting: false,
            statusId: null,
            callback: null
        });
    }, []);

    return {
        closureModal,
        isSubmittingClosure,
        requestClosure,
        confirmClosure,
        cancelClosure
    };
};

export default useClosureComment;
